import { getAccountStorage } from '@juvare/contact-management/backend/features/accounts';
import { defineBulkAction } from '@juvare/platform-sdk-react/backend/features/bulk-actions';
import { isUuid } from '../../../../utils';
import { searchAlertResponders } from '../../alertServiceClient';
import { externalIdPrefixForAlertService, getExternalRecipientsStorage } from '../../entities/ExternalRecipient';
import { getFollowUpAccountMapStorage } from '../../entities/FollowUpAccountMap';
import { createTokenRequest } from '../../utils/createTokenRequest';
import { searchAlertRespondersPageSize } from '../constants';
import { addAlertRespondersToFollowUpBulkActionType, } from './types';
/**
 * This is also hardcoded in Alert Service backend.
 */
const ccEmailPrefix = 'cc_email_';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const addAlertRespondersToFollowUp = (config) => defineBulkAction({
    type: addAlertRespondersToFollowUpBulkActionType,
    onAuthRunSync: (_userContext, { data }) => {
        return data.params.responderCount <= searchAlertRespondersPageSize;
    },
    onAuthBulkAction: (userContext) => {
        return {
            createTokenUserData: {
                userId: userContext.userState.id,
                displayName: userContext.userState.displayName,
                permissions: {
                    systemPermissions: userContext.userState.systemPermissions,
                    orgPermissions: userContext.userState.orgPermissions,
                },
            },
        };
    },
    onProcessBulkAction: (appContext, { data, isCancelled, updateTotalCount, updateProcessedCount, saveResult }) => {
        const accountStorage = getAccountStorage(appContext);
        const externalRecipientsStorage = getExternalRecipientsStorage(appContext);
        const followUpAccountMapStorage = getFollowUpAccountMapStorage(appContext);
        const result = {
            followUpId: appContext.utils.generateRandomUuid(),
        };
        let processedCount = 0;
        let stop = false;
        const processResponders = (externalIds) => {
            // Responder can be:
            // - account - external id is just a UUID
            // - external recipient from CSV file - external id has a special prefix followed by UUID
            // - CC email - external id has a special prefix followed by email
            // Code below has 3 parts to process the responders
            var _a, _b;
            // Part 1 - process accounts
            const validAccountIds = externalIds.filter((id) => isUuid(id));
            if (validAccountIds.length > 0) {
                const accounts = accountStorage.list({
                    filter: [
                        { column: 'systemStatus', op: '=', value: 'active' },
                        { op: 'in', id: validAccountIds },
                    ],
                });
                if (isCancelled()) {
                    stop = true;
                    return;
                }
                followUpAccountMapStorage.massCreate(accounts.rows.map((row) => ({
                    followUpId: result.followUpId,
                    accountId: row.id,
                    alertId: data.params.alertId,
                })));
                if (isCancelled()) {
                    stop = true;
                    return;
                }
            }
            // Part 2 - process external recipients from CSV file
            const validExternalRecipientIds = externalIds
                .filter((id) => id === null || id === void 0 ? void 0 : id.startsWith(externalIdPrefixForAlertService))
                .map((id) => id === null || id === void 0 ? void 0 : id.substring(externalIdPrefixForAlertService.length))
                .filter((id) => isUuid(id));
            if (validExternalRecipientIds.length > 0) {
                // This code copies external recipients. Reading is inefficient, cannot do better without changing Platform.
                // When Platform adds support for bulk reading data from entities then this code needs to be changed to use
                // that new feature.
                if (!result.importId) {
                    result.importId = appContext.utils.generateRandomUuid();
                    result.externalRecipientsRowCount = 0;
                }
                const newExternalRecipientList = [];
                for (const externalRecipientId of validExternalRecipientIds) {
                    try {
                        // Unfortunately this results in N+1 queries. As above comment says, cannot do better for now.
                        const prevExternalRecipient = externalRecipientsStorage.get(externalRecipientId).data;
                        newExternalRecipientList.push(Object.assign(Object.assign({}, prevExternalRecipient), { alertId: undefined, importId: result.importId }));
                    }
                    catch (error) {
                        console.warn(`Failed to get external recipient for follow-up, alertId=${data.params.alertId} externalRecipientId=${externalRecipientId}`, error);
                    }
                }
                externalRecipientsStorage.massCreate(newExternalRecipientList);
                result.externalRecipientsRowCount = ((_a = result.externalRecipientsRowCount) !== null && _a !== void 0 ? _a : 0) + newExternalRecipientList.length;
                if (isCancelled()) {
                    return;
                }
            }
            // Part 3 - process CC emails
            const validCcEmails = externalIds
                .filter((id) => id === null || id === void 0 ? void 0 : id.startsWith(ccEmailPrefix))
                .map((id) => id === null || id === void 0 ? void 0 : id.substring(ccEmailPrefix.length));
            if (validCcEmails.length > 0) {
                result.ccEmails = ((_b = result.ccEmails) !== null && _b !== void 0 ? _b : '') + validCcEmails.join(',');
            }
            processedCount += externalIds.length;
            updateProcessedCount(processedCount);
        };
        if (data.params.externalIds) {
            updateTotalCount(data.params.externalIds.length);
            processResponders(data.params.externalIds);
        }
        else {
            const getRespondersPage = (page) => searchAlertResponders(appContext, createTokenRequest({
                context: appContext,
                userId: data.secureParams.createTokenUserData.userId,
                userPermissions: data.secureParams.createTokenUserData.permissions,
            }), { alertId: data.params.alertId, page: page, size: searchAlertRespondersPageSize }, Object.assign(Object.assign({}, data.params.request), { alertResponderFields: ['externalId'] }));
            let page = 0;
            while (!stop) {
                const responders = getRespondersPage(page);
                if (isCancelled()) {
                    return;
                }
                if (page === 0) {
                    updateTotalCount(responders.totalCount);
                }
                if (responders.items.length > 0) {
                    processResponders(responders.items.map((responder) => responder.externalId));
                }
                if (responders.offset * searchAlertRespondersPageSize + searchAlertRespondersPageSize < responders.totalCount) {
                    ++page;
                }
                else {
                    break;
                }
            }
        }
        saveResult(result);
    },
});
